import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaUserShield,
  FaBuilding,
  FaIdBadge,
  FaMapMarkerAlt,
  FaVenusMars,
  FaCalendar,
  FaImage,
  FaCheck,
  FaTimes,
  FaExclamationCircle,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import useAuth from "../../useAuth";
import { server } from "../../server";

export default function UserForm({ user, userToEdit, onClose, onSuccess }) {
  const targetUser = user || userToEdit;
  const isEdit = !!targetUser;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      gender: "Male",
      isActive: true,
    },
  });

  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    const fetchDependencies = async () => {
      if (!token) return;
      try {
        const [rolesRes, deptRes, empRes] = await Promise.all([
          axios.get(`${server}/roles`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${server}/departments`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${server}/employees`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setRoles(rolesRes.data?.data || []);
        setDepartments(deptRes.data?.data || []);
        setEmployees(empRes.data?.data || []);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load roles and departments");
      }
    };

    fetchDependencies();
  }, [token]);

  useEffect(() => {
    if (targetUser) {
      let roleIdVal = "";
      if (targetUser.roleId) {
        roleIdVal = String(targetUser.roleId);
      } else if (typeof targetUser.role === "object" && targetUser.role?.id) {
        roleIdVal = String(targetUser.role.id);
      } else if (targetUser.role && roles.length > 0) {
        const matched = roles.find(
          (r) =>
            String(r.id) === String(targetUser.role) ||
            r.name.toLowerCase() === String(targetUser.role).toLowerCase()
        );
        roleIdVal = matched ? String(matched.id) : String(targetUser.role);
      } else if (targetUser.role) {
        roleIdVal = String(targetUser.role);
      }

      reset({
        fullName: targetUser.fullName || targetUser.name || targetUser.username || "",
        email: targetUser.email || "",
        mobileNo: targetUser.mobileNo || "",
        roleId: roleIdVal,
        designation: targetUser.designation || "",
        departmentId: targetUser.departmentId ? String(targetUser.departmentId) : "",
        employeeId: targetUser.employeeId ? String(targetUser.employeeId) : "",
        gender: targetUser.gender || "Male",
        dateOfBirth: targetUser.dateOfBirth || "",
        address: targetUser.address || "",
        profilePictureUrl: targetUser.profilePictureUrl || "",
        isActive: targetUser.isActive !== false,
      });
    }
  }, [targetUser, roles, reset]);

  const onSubmit = async (data) => {
    try {
      if (isEdit) {
        const payload = {
          fullName: data.fullName,
          email: data.email,
          mobileNo: data.mobileNo,
          roleId: Number(data.roleId),
          designation: data.designation || "",
          isActive: data.isActive !== false,
        };

        await axios.put(`${server}/auth/users/${targetUser.id}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        toast.success("User account updated successfully!");
      } else {
        const payload = {
          fullName: data.fullName,
          email: data.email,
          mobileNo: data.mobileNo,
          password: data.password,
          roleId: Number(data.roleId),
          designation: data.designation || "",
          departmentId: data.departmentId ? Number(data.departmentId) : null,
          employeeId: data.employeeId ? Number(data.employeeId) : null,
          profilePictureUrl: data.profilePictureUrl || null,
          address: data.address || null,
          gender: data.gender || null,
          dateOfBirth: data.dateOfBirth || null,
        };

        await axios.post(`${server}/auth/register`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        toast.success("User account registered successfully!");
      }

      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error saving user account.");
      console.error(error);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl mx-auto overflow-hidden border border-gray-200 dark:border-slate-800 font-sans transition-colors duration-300">
      {/* Modal Header */}
      <div className="flex justify-between items-center px-5 py-3 border-b border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/80">
        <h3 className="text-sm font-black text-gray-900 dark:text-white flex items-center gap-2">
          <FaUserShield className="text-indigo-600 dark:text-indigo-400" />
          {isEdit ? `Edit User Account (@${targetUser.username || targetUser.email})` : "Register New System User"}
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 text-xs font-bold transition"
        >
          <FaTimes />
        </button>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="p-5 space-y-4 max-h-[82vh] overflow-y-auto custom-scrollbar text-xs"
      >
        {/* Basic Information */}
        <div>
          <h4 className="text-[10px] font-black uppercase text-gray-400 dark:text-slate-400 tracking-wider mb-2 border-b border-gray-100 dark:border-slate-800 pb-1">
            Account Identity & Contact
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Full Name */}
            <div>
              <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                Full Name <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <input
                  {...register("fullName", { required: "Full name is required" })}
                  className={`w-full p-2.5 bg-gray-50 dark:bg-slate-800 border ${
                    errors.fullName
                      ? "border-rose-500 bg-rose-50 dark:bg-rose-950/30"
                      : "border-gray-200 dark:border-slate-700"
                  } rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition`}
                  placeholder="e.g. John Doe"
                />
                {errors.fullName && (
                  <div className="absolute right-3 top-2.5 text-rose-500">
                    <FaExclamationCircle size={11} />
                  </div>
                )}
              </div>
              {errors.fullName && (
                <p className="text-[10px] text-rose-500 mt-1 font-semibold">{errors.fullName.message}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                Email Address <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <input
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                  type="email"
                  className={`w-full p-2.5 bg-gray-50 dark:bg-slate-800 border ${
                    errors.email
                      ? "border-rose-500 bg-rose-50 dark:bg-rose-950/30"
                      : "border-gray-200 dark:border-slate-700"
                  } rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition`}
                  placeholder="user@example.com"
                />
              </div>
              {errors.email && (
                <p className="text-[10px] text-rose-500 mt-1 font-semibold">{errors.email.message}</p>
              )}
            </div>

            {/* Mobile Number */}
            <div>
              <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                Mobile Contact <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <input
                  {...register("mobileNo", {
                    required: "Mobile number is required",
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: "Must be 10 digits",
                    },
                  })}
                  className={`w-full p-2.5 bg-gray-50 dark:bg-slate-800 border ${
                    errors.mobileNo
                      ? "border-rose-500 bg-rose-50 dark:bg-rose-950/30"
                      : "border-gray-200 dark:border-slate-700"
                  } rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition`}
                  placeholder="9876543210"
                  maxLength={10}
                />
              </div>
              {errors.mobileNo && (
                <p className="text-[10px] text-rose-500 mt-1 font-semibold">{errors.mobileNo.message}</p>
              )}
            </div>

            {/* Password (Only for new user) */}
            {!isEdit && (
              <div>
                <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                  Password <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <input
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Minimum 6 characters",
                      },
                    })}
                    type={showPassword ? "text" : "password"}
                    className={`w-full p-2.5 pr-10 bg-gray-50 dark:bg-slate-800 border ${
                      errors.password
                        ? "border-rose-500 bg-rose-50 dark:bg-rose-950/30"
                        : "border-gray-200 dark:border-slate-700"
                    } rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 transition focus:outline-none"
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-[10px] text-rose-500 mt-1 font-semibold">{errors.password.message}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Role & Access Permissions */}
        <div>
          <h4 className="text-[10px] font-black uppercase text-gray-400 dark:text-slate-400 tracking-wider mb-2 border-b border-gray-100 dark:border-slate-800 pb-1">
            System Role & Organization
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Role Select */}
            <div>
              <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                Access Role <span className="text-rose-500">*</span>
              </label>
              <select
                {...register("roleId", { required: "Role is required" })}
                className={`w-full p-2.5 bg-gray-50 dark:bg-slate-800 border ${
                  errors.roleId
                    ? "border-rose-500 bg-rose-50 dark:bg-rose-950/30"
                    : "border-gray-200 dark:border-slate-700"
                } rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition`}
              >
                <option value="">Select System Role</option>
                {roles.map((role) => (
                  <option key={role.id} value={String(role.id)}>
                    {role.name}
                  </option>
                ))}
              </select>
              {errors.roleId && (
                <p className="text-[10px] text-rose-500 mt-1 font-semibold">{errors.roleId.message}</p>
              )}
            </div>

            {/* Designation */}
            <div>
              <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                Designation
              </label>
              <input
                {...register("designation")}
                className="w-full p-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition"
                placeholder="e.g. Senior Administrator, Accountant"
              />
            </div>
          </div>
        </div>

        {/* Active Toggle Switch */}
        <div className="flex items-center gap-2.5 p-2.5 bg-indigo-50 dark:bg-indigo-950/40 rounded-xl border border-indigo-100 dark:border-indigo-900/50">
          <input
            type="checkbox"
            id="isActiveUser"
            {...register("isActive")}
            className="w-4 h-4 text-indigo-600 dark:text-indigo-400 rounded border-gray-300 dark:border-slate-700 focus:ring-indigo-500 cursor-pointer"
          />
          <label
            htmlFor="isActiveUser"
            className="text-xs font-bold text-gray-700 dark:text-slate-200 cursor-pointer select-none"
          >
            Mark Account as Active (Unchecked will suspend API access)
          </label>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 rounded-xl font-bold text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-xl font-black transition shadow-xs flex items-center gap-1.5 disabled:opacity-50"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-1.5">
                <svg className="animate-spin h-3.5 w-3.5 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            ) : (
              <>
                <FaCheck size={10} /> {isEdit ? "Update User Account" : "Register User"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

